import { take, put, call, select, cancel, fork, all } from 'redux-saga/effects';
import { takeEvery, delay } from 'redux-saga';
import * as Api from 'api';
import * as Actions from 'actions';
import * as ActionTypes from 'constants';
import unkinkPolygon from '@turf/unkink-polygon';
import { polygon } from '@turf/helpers';
import simplify from '@turf/simplify';
import ToGeoJson from '@mapbox/togeojson';

/***************************** Selectors ************************************/

export const getMapCreator = state => (state.mapCreator);
export const getUsers = state => (state.users);
// export const getRoles = state => (state.roleManagement)

/******************************* Helpers **************************************/

const removeDuplicateCoords = (coords) => {
	const clean = [];
	for(const coord of coords) {
		let found = 0;
		for(let i = clean.length - 1; i >= 0; i--) {
			if(clean[i][0] == coord[0] && clean[i][1] == coord[1]) {
				found = 1;
				break;
			}
		}
		if(!found) {
			clean.push(coord);
		}
	}
	clean.push(clean[0]);
	return clean;
}

const fileReaderPromise = (reader, file) => new Promise((res, rej) => {
	reader.onload = e => {
		const parser = new DOMParser();
		const xml = parser.parseFromString(e.target.result, 'text/xml');
		const featureCollection = ToGeoJson.kml(xml);
		const processed_features = featureCollection.features
		.filter(feature => feature.geometry.type == 'Polygon')
		.map(feature => {
			feature.geometry.coordinates[0] = removeDuplicateCoords(feature.geometry.coordinates[0]);
			const poly = polygon(feature.geometry.coordinates, feature.properties);
			const result = unkinkPolygon(simplify(poly, 1 / feature.geometry.coordinates[0].length, false));
			return result.features;
		});
		res(processed_features);
	}
	reader.onerror = rej;
	reader.readAsText(file);
})

/***************************** Subroutines ************************************/

function * initMapCreator() {
	try {
		const { storm_date } = yield select(getMapCreator);
		yield call(getMapsForDate, { payload: storm_date });
		const { markers } = yield call(Api.fetchMapCreatorMarkerTypes);
		yield put(Actions.initMapCreatorMarkerTypes(markers));
	}
	catch(ex) {
		call(eventFailed, { payload: ex });
	}
}

function * createMapSuccess({ payload }) {
	const { map, user_id } = payload;
	const { user } = yield select(getUsers);
	if(user_id == user._id) {
		yield put(Actions.addNewMap(map, true));
	}
	else {
		yield put(Actions.addNewMap(map, false));
	}
}

function * getMapsForDate({ payload: date }) {
	try {
		const maps = (yield call(Api.searchMapByStormDate, date))
		.map(m => {
			m.features = m.features.map(f => {
				if(f._id != null) {
					f.properties._id = f._id;
				}
				else if(f.properties._id != null) {
					f._id = f.properties._id;
				}
				return f;
			});
			return m;
		});
		yield put(Actions.initMapCreatorMapList(maps));
	}
	catch(ex) {
		yield call(eventFailed, { payload: ex });
	}
}

function * createMapFeature({ payload }) {
	const { feature, map_id } = payload;
	if(feature.geometry.type == 'Point') {
		const { current_marker_type } = yield select(getMapCreator);
		feature.properties = { ...(feature.properties || {}), icon_file: current_marker_type.file_data._id };
	}

	yield put(Actions.sendSocketEmit({ feature, map_id }, 'create-map-feature'));
}

function * updateCurrentMapFeature(action) {
	try {
		const { feature, map_id } = action.payload;
		yield put(Actions.sendSocketEmit({ feature, map_id }, 'edit-map-feature'));
	}
	catch(ex) {
		console.log(ex);
		yield call(eventFailed, { payload: ex });
	}
}

function * editMapFeatureSuccess({ payload }) {
	const { user } = yield select(getUsers);
	if(user._id == payload.user_id) {
		yield put(Actions.setMapCreatorProperties({
			show_marker_editor_icon_modal: false,
			show_marker_editor_info_window_modal: false,
			tmp_info_window_image_file: null,
			show_marker_editor_info_window_modal: false,
			show_delete_map_creator_marker_type_modal: false
		}));
	}
}

function * uploadKMLLayers({ payload }) {
	const { map_id, accepted_files, rejected_files } = payload;
	try {
		yield put(Actions.toggleUploadingKMLLayers(true));
		const file_data = accepted_files.map(file => {
			const form = new FormData();
			form.append('properties.kml_layers', file, file.name);
			return form;
		});
		yield all(file_data.map(data => {
			return call(Api.callApi, 'POST', `/upload/map/${map_id}/properties.kml_layers`, data);
		}));
		const updated_map = yield call(Api.fetchMapById, map_id);
		if(updated_map && updated_map.properties) {
			yield put(Actions.updateMapProperties(updated_map.properties, map_id));
		}
	}
	catch(ex) {
		console.log(ex);
		yield put(Actions.notify(ex.message || ex));
	}
	finally {
		yield put(Actions.toggleUploadingKMLLayers(false));
	}
}

function * removeKMLLayer({ payload }) {
	try {
		const { layer_id, map_id } = payload;
		yield put(Actions.toggleDeletingKMLLayers(true));
		yield call(Api.deleteFile, 'map', map_id, 'properties.kml_layers', layer_id);
		const updated_map = yield call(Api.fetchMapById, map_id);
		yield put({ type: ActionTypes.EDIT_MAP_SUCCESS, payload: { map: updated_map } });
		yield put(Actions.toggleDeletingKMLLayers(false));
	}
	catch(ex) {
		yield call(eventFailed, { payload: ex });
	}
}

function * uploadPolygons({ payload }) {
	const { map_id, accepted_files, rejected_files } = payload;
	try {
		yield put(Actions.toggleUploadingPolygons(true));
		const reader = new FileReader();
		const processed_feature_data = yield all(accepted_files.map(file => call(fileReaderPromise, reader, file)));
		for(const data of processed_feature_data) {
			for(const features of data) {
				yield all(
					features.map(f => put(Actions.sendSocketEmit({ map_id, feature: f }, 'create-map-feature')))
				)
			}
		}
	}
	catch(ex) {
		console.log(ex);
		yield put(Actions.notify(ex.message || ex));
	}
	finally {
		yield put(Actions.toggleUploadingPolygons(false));
	}
}

function * uploadMapCreatorMarker({ payload }) {
	try {
		const { accepted_files, rejected_files } = payload;
		yield put(Actions.toggleProcessingMapCreatorMarker(true));
		const file_data = accepted_files.map(file => {
			const form = new FormData();
			form.append('file_data', file, file.name);
			return form;
		});
		yield all(file_data.map(data => call(Api.createMarkerType, data)));
		const { markers } = yield call(Api.fetchMapCreatorMarkerTypes);
		yield put(Actions.initMapCreatorMarkerTypes(markers));
	}
	catch(ex) {
		yield call(eventFailed, { payload: ex });
	}
	finally {
		yield put(Actions.toggleProcessingMapCreatorMarker(false));
	}
}

function * deleteMapCreatorMarkerType({ payload: marker_id }) {
	try {
		const { current_marker_type } = yield select(getMapCreator);
		const deleted_marker_id = yield call(Api.deleteMarkerType, current_marker_type._id);
		yield put({ type: ActionTypes.DELETE_MAP_CREATOR_MARKER_TYPE_SUCCESS, payload: deleted_marker_id });
	}
	catch(ex) {
		call(eventFailed, { payload: ex });
	}
	finally {
		yield put(Actions.toggleShowDeleteMapCreatorMarkerTypeModal(false));
	}
}

function * uploadMapCreatorInfoWindowImage({ payload }) {
	try {
		const { current_map, current_feature, tmp_info_window_image } = yield select(getMapCreator);
		const { accepted_files, rejected_files } = payload;
		yield put(Actions.toggleUploadingMapCreatorInfoWindowImage(true));
		if(tmp_info_window_image) {
			yield call(Api.callApi, 'DELETE', `/files/delete-file/${tmp_info_window_image_file._id}`);
			yield put(Actions.updateMapCreatorTmpInfoWindowImage(null));
		}
		const file = accepted_files[0];
		const form = new FormData();
		form.append('properties.info_window_image_file', file, file.name);
		const saved_file = yield call(Api.uploadMaCreatorInfoWindowImage, current_map._id, current_feature._id || current_feature.id || current_feature.properties._id, form);
		yield put(Actions.updateMapCreatorTmpInfoWindowImage(saved_file));
	}
	catch(ex) {
		yield call(eventFailed, { payload: ex });
	}
	finally {
		yield put(Actions.toggleUploadingMapCreatorInfoWindowImage(false));
	}
}

function * cancelUpdateMapCreatorInfoWindow() {
	const { tmp_info_window_image_file } = yield select(getMapCreator);
	yield put(Actions.toggleShowMarkerEditorInfoWindowModal(false));
	if(tmp_info_window_image_file) {
		yield call(Api.callApi, 'DELETE', `/files/delete-file/${tmp_info_window_image_file._id}`);
	}
	yield put(Actions.updateMapCreatorTmpInfoWindowImage(null));
}

function * toggleMarkCurrentMapFinished({ payload: map_finished }) {
	try {
		const { current_map } = yield select(getMapCreator);
		const updated_properties = {
			...current_map.properties,
			finished: map_finished
		};
		yield put(Actions.updateCurrentMapProperties(updated_properties));
		try {
			if(map_finished) {
				yield call(Api.markMapAsFinished, current_map._id)
			}
			else {
				yield call(Api.markMapAsUnFinished, current_map._id)
			}
		}
		catch(ex) {
			console.log(ex);
			yield call(eventFailed, { payload: ex });
			const fetched_map = yield call(fetchMapById, current_map._id);
			yield put(Actions.updateMapProperties(fetched_map.properties, fetched_map._id));
		}
	}
	catch(ex) {
		console.log(ex);
		yield call(eventFailed, { payload: ex });
	}
}

function * confirmEditMapWarning() {
	try {
		const { tmp_map_id } = yield select(getMapCreator);
		yield put(Actions.toggleWaitingForMapCreatorResponse(true));
		yield call(Api.markMapAsUnFinished, tmp_map_id);
		yield put(Actions.updateMapProperties({ finished: false }, tmp_map_id));
		yield put(Actions.initEditMap(tmp_map_id));
	}
	catch(ex) {
		yield call(eventFailed, { payload: ex });
	}
	finally {
		yield put(Actions.toggleWaitingForMapCreatorResponse(false));
	}
}

function * eventFailed(action) {
	let message = action.payload.message || action.payload;
	console.log(action.payload);
	yield put(Actions.notify({ message }));
}

/******************************************************************************/
/******************************* WATCHERS *************************************/
/******************************************************************************/

export function* mapCreatorWatcher() {
  yield* [
		takeEvery(ActionTypes.INIT_MAP_CREATOR, initMapCreator),
		takeEvery(ActionTypes.UPDATE_MAP_STORM_DATE, getMapsForDate),
		takeEvery(ActionTypes.CREATE_MAP_SUCCESS, createMapSuccess),
		takeEvery(ActionTypes.CREATE_MAP_FEATURE, createMapFeature),
		takeEvery(ActionTypes.UPDATE_CURRENT_MAP_FEATURE, updateCurrentMapFeature),
		takeEvery(ActionTypes.EDIT_MAP_FEATURE_SUCCESS, editMapFeatureSuccess),
		takeEvery(ActionTypes.UPLOAD_KML_LAYERS, uploadKMLLayers),
		takeEvery(ActionTypes.REMOVE_KML_LAYER, removeKMLLayer),
		takeEvery(ActionTypes.UPLOAD_POLYGONS, uploadPolygons),
		takeEvery(ActionTypes.UPLOAD_MAP_CREATOR_MARKER, uploadMapCreatorMarker),
		takeEvery(ActionTypes.DELETE_MAP_CREATOR_MARKER_TYPE, deleteMapCreatorMarkerType),
		takeEvery(ActionTypes.UPLOAD_MAP_CREATOR_INFO_WINDOW_IMAGE, uploadMapCreatorInfoWindowImage),
		takeEvery(ActionTypes.CANCEL_UPDATE_MAP_CREATOR_INFO_WINDOW, cancelUpdateMapCreatorInfoWindow),
		takeEvery(ActionTypes.TOGGLE_MARK_CURRENT_MAP_FINISHED, toggleMarkCurrentMapFinished),
		takeEvery(ActionTypes.CONFIRM_EDIT_MAP_WARNING, confirmEditMapWarning),
  	takeEvery(ActionTypes.CREATE_MAP_FAILURE, eventFailed),
		takeEvery(ActionTypes.EDIT_MAP_FAILURE, eventFailed),
		takeEvery(ActionTypes.DELETE_MAP_FEATURE_FAILURE, eventFailed),
		takeEvery(ActionTypes.EDIT_MAP_FEATURE_FAILURE, eventFailed),
		takeEvery(ActionTypes.MOVE_MAP_FEATURE_FAILURE, eventFailed)
  ]
};



// WEBPACK FOOTER //
// ./src/common/sagas/map-creator.js